// Growlab 811 — 3D kinetic model of the tropical-ecosystem build directive.
// Route: /growlab. Every device from the directive is placed, labeled, and in motion.
import React, { useMemo, useRef, useState, useEffect, useCallback, Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Grid } from "@react-three/drei";
import { DEVICES, TREES } from "../components/growlab/data";
import {
  DeviceLabel, SpinRing, T8Unit, RaxialUnit, CloudrayUnit,
  CloudforgeUnit, ControllerUnit, OutletUnit, MHFixture, CMHFixture,
  Tree, Tent, SoilBed, GrowLights, ETFEShell, DevicePhoto, TreePhoto, FrameHooks,
} from "../components/growlab/GrowlabParts";
import { AirflowParticles, VaporPlume, UISLinks, CameraRig } from "../components/growlab/GrowlabEffects";
import { Header, EquipmentIndex, InspectorCard, BottomBar, TourSlide } from "../components/growlab/GrowlabPanels";
import RetrofitGallery from "../components/growlab/RetrofitGallery";

const UNITS = { t8: T8Unit, raxial: RaxialUnit, cloudray: CloudrayUnit, cloudforge: CloudforgeUnit, controller: ControllerUnit, outlet: OutletUnit, mh1000: MHFixture, cmh315: CMHFixture };
const PHOTO_W = { t8: 2.1, raxial: 1.5, cloudray: 1.5, cloudforge: 1.5, controller: 1.7, outlet: 1.0, mh1000: 2.6, cmh315: 1.9 };
const HOME = { pos: [11, 6.5, 13], target: [0, 0.2, 0] };

function focusFor(id) {
  const d = DEVICES.find((x) => x.id === id);
  if (!d) return HOME;
  const [x, y, z] = d.pos;
  const len = Math.hypot(x, z) || 1;
  return { pos: [x + (x / len) * 3.4, y + 1.6, z + (z / len) * 3.4], target: [x, y, z] };
}

export default function Growlab() {
  const [selected, setSelected] = useState(null);
  const [focus, setFocus] = useState(null);
  const [tour, setTour] = useState(false);
  const [running, setRunning] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [dense, setDense] = useState(true);
  const [walls, setWalls] = useState(false);
  const [retrofit, setRetrofit] = useState(() => new URLSearchParams(window.location.search).get("retrofit") === "1");
  const [etfe, setEtfe] = useState(false);
  const [realistic, setRealistic] = useState(true);
  const controlsRef = useRef();

  const select = useCallback((id) => {
    setSelected(id);
    setFocus(id ? focusFor(id) : null);
  }, []);

  useEffect(() => {
    if (!tour) return undefined;
    let i = 0;
    select(DEVICES[0].id);
    const id = setInterval(() => {
      i = (i + 1) % DEVICES.length;
      select(DEVICES[i].id);
    }, 4600);
    return () => clearInterval(id);
  }, [tour, select]);

  const stopTour = useCallback(() => setTour(false), []);
  const scene = useMemo(() => DEVICES, []);

  return (
    <div className="h-[100dvh] w-screen flex flex-col overflow-hidden bg-[#090c10]" data-testid="growlab-page">
      <Header tour={tour} onTour={() => setTour((t) => !t)} />
      <div className="relative flex-1 min-h-0">
        <Canvas
          data-testid="growlab-canvas"
          camera={{ position: HOME.pos, fov: 45 }}
          dpr={[1, 2]}
          style={{ position: "absolute", inset: 0 }}
          onPointerMissed={() => setSelected(null)}
        >
          <color attach="background" args={["#090c10"]} />
          <fog attach="fog" args={["#090c10", 20, 46]} />
          <ambientLight intensity={0.55} />
          <directionalLight position={[6, 12, 6]} intensity={0.7} />
          <GrowLights />
          <Grid position={[0, -4.55, 0]} args={[60, 60]} cellColor="#1c1917" sectionColor="#3f3a2a" fadeDistance={38} infiniteGrid />
          <Tent showWalls={walls && !etfe} />
          <ETFEShell visible={etfe} />
          <FrameHooks shellOn={etfe} />
          <SoilBed />
          <Suspense fallback={null}>
            {TREES.map((t) => realistic
              ? <TreePhoto key={t.name} tree={t} running={running} />
              : <Tree key={t.name} tree={t} running={running} />)}
            {scene.map((d) => {
              const Unit = UNITS[d.id];
              return (
                <group
                  key={d.id}
                  position={d.pos}
                  onClick={(e) => { e.stopPropagation(); stopTour(); select(d.id); }}
                >
                  {realistic ? <DevicePhoto id={d.id} width={PHOTO_W[d.id] || 1.8} /> : <Unit running={running} />}
                  {realistic && (d.id === "mh1000" || d.id === "cmh315") && (
                    <pointLight position={[0, -1.2, 0]} intensity={d.id === "mh1000" ? 26 : 20} distance={12} color={d.id === "mh1000" ? "#bae6fd" : "#fcd34d"} />
                  )}
                  {selected === d.id && <SpinRing color={d.color} />}
                  {selected === d.id && <pointLight intensity={6} distance={6} color={d.color} />}
                  <DeviceLabel id={d.id} name={d.name} selected={selected === d.id} onSelect={(x) => { stopTour(); select(x); }} y={d.id === "outlet" ? 1.1 : 1.5} />
                </group>
              );
            })}
          </Suspense>
          <AirflowParticles running={running} dense={dense} speed={speed} />
          <VaporPlume running={running} />
          <UISLinks selected={selected} />
          <CameraRig focus={focus} controlsRef={controlsRef} />
          <OrbitControls
            ref={controlsRef}
            makeDefault
            enableDamping
            minDistance={4}
            maxDistance={30}
            maxPolarAngle={Math.PI / 2 + 0.05}
            onStart={() => { setTour(false); setFocus(null); }}
          />
        </Canvas>
        <EquipmentIndex selected={selected} onSelect={(id) => { stopTour(); select(id); }} />
        <InspectorCard selected={selected} onClose={() => setSelected(null)} />
        {tour && selected && (
          <TourSlide
            deviceId={selected}
            index={DEVICES.findIndex((d) => d.id === selected)}
            total={DEVICES.length}
            onStop={() => setTour(false)}
          />
        )}
        <div className={`md:hidden absolute left-2 right-2 bottom-2 gap-2 overflow-x-auto z-10 pb-1 ${selected ? "hidden" : "flex"}`} data-testid="equipment-chips-mobile">
          {DEVICES.map((d) => (
            <button
              key={d.id}
              data-testid={`chip-${d.id}`}
              onClick={() => { stopTour(); select(d.id); }}
              className={`px-3 py-2 rounded-lg font-mono text-[10px] tracking-widest whitespace-nowrap border min-h-[44px] ${
                selected === d.id ? "bg-amber-400 text-stone-950 border-amber-300" : "bg-stone-950/85 text-amber-300/90 border-amber-500/30"
              }`}
            >
              {d.name}
            </button>
          ))}
        </div>
      </div>
      <BottomBar
        running={running} onRun={() => setRunning((r) => !r)}
        speed={speed} onSpeed={() => setSpeed((s) => (s === 1 ? 2 : 1))}
        dense={dense} onDense={() => setDense((v) => !v)}
        walls={walls} onWalls={() => setWalls((v) => !v)}
        etfe={etfe} onEtfe={() => setEtfe((v) => !v)}
        realistic={realistic} onRealistic={() => setRealistic((v) => !v)}
        onReset={() => { stopTour(); setSelected(null); setFocus(HOME); }}
        onRetrofit={() => { stopTour(); setRetrofit(true); }}
      />
      {retrofit && <RetrofitGallery onClose={() => setRetrofit(false)} />}
    </div>
  );
}
